import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    ListItemSecondaryAction,
    Avatar,
    Badge,
    Chip,
    IconButton,
    Tooltip,
    Divider,
    Alert,
    CircularProgress,
    Paper,
    Stack,
    Tabs,
    Tab,
} from "@mui/material";
import {
    LocationOn as LocationOnIcon,
    MyLocation as MyLocationIcon,
    Support as SupportIcon,
    ArrowBack as ArrowBackIcon,
    Refresh as RefreshIcon,
    OnlinePrediction as OnlineIcon,
    OfflineBolt as OfflineIcon,
    Map as MapIcon,
    List as ListIcon,
} from "@mui/icons-material";
import {
    useGetAllTrackingDataQuery,
    useGetTrackingStatsQuery,
} from "../../../store/tracking/trackingApis";
import LiveTrackingMap from "./LiveTrackingMap";

const SimpleTrackingDashboard = ({ targetUserId }) => {
    const navigate = useNavigate();
    const [lastRefresh, setLastRefresh] = useState(new Date());
    const [viewMode, setViewMode] = useState(0); // 0 = List, 1 = Map
    const [selectedUserForMap, setSelectedUserForMap] = useState(null);

    // Fetch tracking data with manual refresh
    const {
        data: trackingData,
        isLoading: trackingLoading,
        error: trackingError,
        refetch: refetchTrackingData,
    } = useGetAllTrackingDataQuery(undefined, {
        pollingInterval: 0, // No polling - manual refresh only
    });

    const {
        data: statsData,
        isLoading: statsLoading,
        error: statsError,
        refetch: refetchStats,
    } = useGetTrackingStatsQuery(undefined, {
        pollingInterval: 0, // No polling - manual refresh only
    });

    // Extract data from API responses
    const connectedUsers = trackingData?.connectedUsers || [];
    const userLocations = trackingData?.userLocations || {};
    // Use stats from main response if available, otherwise use stats query
    const trackingStats = trackingData?.trackingStats || statsData || {
        totalConnected: 0,
        totalStaffOnline: 0,
        lastUpdated: null,
    };
    const isLoading = trackingLoading || statsLoading;
    const error = trackingError || statsError;

    // Manual refresh handler
    const handleManualRefresh = () => {
        refetchTrackingData();
        refetchStats();
        setLastRefresh(new Date());
    };

    // Handle view on map
    const handleViewOnMap = (user) => {
        setSelectedUserForMap(user);
        setViewMode(1); // Switch to map view
    };

    // Convert location data format if needed
    const formatLocation = (location) => {
        if (!location) return null;
        // If location is already in the correct format
        if (location.latitude && location.longitude) {
            return {
                latitude: parseFloat(location.latitude),
                longitude: parseFloat(location.longitude),
                accuracy: location.accuracy ? parseFloat(location.accuracy) : null,
                timestamp: location.timestamp,
            };
        }
        return null;
    };

    const getStatusColor = (isTracking) => {
        return isTracking ? { bg: "#E8F5E8", color: "#2E7D32" } : { bg: "#FFEBEE", color: "#C62828" };
    };

    if (isLoading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        Staff Location Tracking
                    </Typography>
                    <Tabs value={viewMode} onChange={(e, newValue) => setViewMode(newValue)} sx={{ ml: "auto" }}>
                        <Tab icon={<ListIcon />} label="List View" iconPosition="start" />
                        <Tab icon={<MapIcon />} label="Map View" iconPosition="start" />
                    </Tabs>
                </Box>
                <Typography variant="body1" color="textSecondary">
                    View last known locations of staff members. Tap refresh to get the latest data.
                </Typography>
                <Box sx={{ mt: 2, display: "flex", gap: 2, alignItems: "center" }}>
                    <Chip label="📡 HTTP API" color="info" size="small" />
                    <Typography variant="caption" color="textSecondary">
                        Last updated: {lastRefresh.toLocaleTimeString()}
                    </Typography>
                    <IconButton size="small" onClick={handleManualRefresh} disabled={isLoading}>
                        <RefreshIcon />
                    </IconButton>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {typeof error === "string" ? error : error?.message || "An error occurred"}
                </Alert>
            )}

            {/* Status Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={3}>
                    <Paper sx={{ p: 2, textAlign: "center", bgcolor: "#E8F5E8" }}>
                        <Typography variant="h4" color="success.main" fontWeight="bold">
                            {trackingStats.totalStaffOnline || 0}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Staff with Location
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={3}>
                    <Paper sx={{ p: 2, textAlign: "center", bgcolor: "#E3F2FD" }}>
                        <Typography variant="h4" color="primary.main" fontWeight="bold">
                            {connectedUsers.filter((u) => u.isTracking).length}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Active Tracking
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={3}>
                    <Paper sx={{ p: 2, textAlign: "center", bgcolor: "#F5F5F5" }}>
                        <Typography variant="h4" color="text.secondary" fontWeight="bold">
                            {connectedUsers.length}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Total Staff
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={3}>
                    <Paper sx={{ p: 2, textAlign: "center", bgcolor: "#FFF3E0" }}>
                        <Typography variant="h4" color="warning.main" fontWeight="bold">
                            {connectedUsers.length - (trackingStats.totalStaffOnline || 0)}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            No Location Data
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>

            {/* View Mode Content */}
            {viewMode === 0 ? (
                // List View
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                    Staff Status
                                </Typography>

                                <List>
                                    {connectedUsers.length === 0 ? (
                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                            sx={{ textAlign: "center", py: 4 }}
                                        >
                                            No staff members with location data found
                                        </Typography>
                                    ) : (
                                        connectedUsers.map((user, index) => {
                                            // Try userLocations first, then currentLocation from user object
                                            const location = userLocations[user?.id] || userLocations[user?.userId] || user?.currentLocation;
                                            const statusColor = getStatusColor(user?.isTracking);

                                            return (
                                                <React.Fragment key={user?.id || user?.userId}>
                                                    <ListItem>
                                                        <ListItemAvatar>
                                                            <Badge
                                                                color={user?.isTracking ? "success" : "error"}
                                                                variant="dot"
                                                                anchorOrigin={{
                                                                    vertical: "bottom",
                                                                    horizontal: "right",
                                                                }}
                                                            >
                                                                <Avatar sx={{ bgcolor: "#4CAF50" }}>
                                                                    <SupportIcon />
                                                                </Avatar>
                                                            </Badge>
                                                        </ListItemAvatar>
                                                        <ListItemText
                                                            primary={
                                                                typeof user?.name === "string"
                                                                    ? user?.name
                                                                    : user?.id || user?.userId
                                                            }
                                                            secondary={
                                                                <Box>
                                                                    <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                                                                        <Chip
                                                                            label={user?.role?.toUpperCase()}
                                                                            size="small"
                                                                            sx={{ bgcolor: "#F5F5F5", color: "#666" }}
                                                                        />
                                                                        <Chip
                                                                            label={
                                                                                user?.isTracking
                                                                                    ? "Has Location"
                                                                                    : "No Location"
                                                                            }
                                                                            size="small"
                                                                            sx={{
                                                                                bgcolor: statusColor.bg,
                                                                                color: statusColor.color,
                                                                            }}
                                                                        />
                                                                    </Stack>
                                                                    {(() => {
                                                                        const formattedLocation = formatLocation(location);
                                                                        return formattedLocation ? (
                                                                            <Box>
                                                                                <Typography variant="caption" display="block">
                                                                                    📍 {formattedLocation.latitude.toFixed(6)},{" "}
                                                                                    {formattedLocation.longitude.toFixed(6)}
                                                                                </Typography>
                                                                                {formattedLocation.accuracy && (
                                                                                    <Typography variant="caption" display="block">
                                                                                        🎯 Accuracy: {formattedLocation.accuracy}m
                                                                                    </Typography>
                                                                                )}
                                                                                {formattedLocation.timestamp && (
                                                                                    <Typography variant="caption" display="block">
                                                                                        ⏰{" "}
                                                                                        {new Date(
                                                                                            formattedLocation.timestamp,
                                                                                        ).toLocaleTimeString()}
                                                                                    </Typography>
                                                                                )}
                                                                            </Box>
                                                                        ) : (
                                                                            <Typography variant="caption" color="textSecondary">
                                                                                No location data available
                                                                            </Typography>
                                                                        );
                                                                    })()}
                                                                </Box>
                                                            }
                                                        />
                                                        <ListItemSecondaryAction>
                                                            <Box sx={{ display: "flex", gap: 1 }}>
                                                                <Tooltip title="View on Map">
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleViewOnMap(user)}
                                                                        sx={{ color: "#1976d2" }}
                                                                    >
                                                                        <MapIcon />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </Box>
                                                        </ListItemSecondaryAction>
                                                    </ListItem>
                                                    {index < connectedUsers.length - 1 && <Divider />}
                                                </React.Fragment>
                                            );
                                        })
                                    )}
                                </List>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            ) : (
                // Map View
                <LiveTrackingMap
                    targetUserId={selectedUserForMap?.id || selectedUserForMap?.userId || targetUserId}
                    selectedUserFromList={selectedUserForMap}
                    key={selectedUserForMap?.id || selectedUserForMap?.userId || "default"}
                />
            )}
        </Box>
    );
};

export default SimpleTrackingDashboard;
